import React from 'react'

import FormSubtext from 'components/common/FormSubtext'
import theme from 'theme'

const transition = '200ms ease'

function validityStyles(color) {
  const colorChoice = theme.colors[color][0]

  return {
    borderColor: colorChoice,
    color: colorChoice,

    '&:focus-within': {
      borderColor: colorChoice,
      color: colorChoice
    }
  }
}

export const TextAreaContainer: React.FC<{
  className?: string
  disabled?: boolean
  isInvalid?: boolean
  isValid?: boolean
  noLeftRadius?: boolean
  noMargin?: boolean
  noRightRadius?: boolean
  subtext?: boolean
}> = ({
  disabled,
  noRightRadius,
  noLeftRadius,
  noMargin,
  subtext,
  isValid,
  isInvalid,
  ...rest
}) => (
  <div
    css={[
      {
        display: 'flex',
        position: 'relative',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        height: 'auto',
        borderRadius: theme.borderRadius[0],
        backgroundColor: disabled ? theme.colors.gray[0] : 'white',
        border: theme.border.base,
        color: theme.colors.steel[1],
        marginBottom: !noMargin && !subtext && theme.space[4],
        width: '100%',

        '&:focus-within': {
          borderColor: theme.colors.blue[0],
          color: theme.colors.blue[0],
          zIndex: 1,

          label: [
            {
              color: theme.colors.blue[0],
              opacity: 1
            },

            isValid && validityStyles('green'),
            isInvalid && validityStyles('red')
          ]
        }
      },

      noLeftRadius && {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        marginLeft: '-1px'
      },

      noRightRadius && {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0
      },

      isValid && validityStyles('green'),
      isInvalid && validityStyles('red')
    ]}
    {...rest}
  />
)

type TextAreaProps = React.HTMLProps<HTMLTextAreaElement> & {
  hasLabel?: boolean
  hasValue?: boolean
  hideLabelOnFocus?: boolean
}

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ hasLabel, hasValue, hideLabelOnFocus, ...rest }, ref) => (
    <textarea
      ref={ref}
      css={{
        transition,
        backgroundColor: 'transparent',
        fontSize: theme.fontSizes[2],
        color: theme.colors.steel[2],
        fontWeight: theme.fontWeights[0],
        fontStyle: 'normal',
        fontStretch: 'normal',
        fontFamily: theme.fonts.base,
        minHeight: hasLabel && hasValue ? theme.fontSizes[7] : 72,
        height: 'auto',
        lineHeight: '1.2',
        letterSpacing: 'normal',
        paddingLeft: theme.space[3],
        paddingRight: theme.space[3],
        textAlign: 'left',
        outline: 'none',
        border: 0,
        display: 'block',
        width: '100%',
        borderRadius: theme.borderRadius[0],

        '&:focus':
          hasLabel && !hideLabelOnFocus
            ? { marginTop: theme.space[4], minHeight: theme.fontSizes[7] }
            : {},
        marginTop: hasLabel && hasValue ? theme.space[4] : theme.space[3],
        zIndex: 10,

        '&::placeholder': {
          color: theme.colors.steel[1],
          fontWeight: theme.fontWeights[0],
          opacity: theme.opacity[0]
        },

        '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active':
          {
            transition: 'all 5000s ease-in-out 0s',
            transitionProperty: 'background-color, color'
          }
      }}
      {...rest}
    />
  )
)

TextArea.displayName = 'TextArea'

export const TextAreaLabel: React.FC<{
  hasValue?: boolean
  hideLabelOnFocus?: boolean
}> = ({ hasValue, hideLabelOnFocus, ...rest }) => (
  <label
    css={{
      transition,
      display: 'block',
      position: 'absolute',
      fontSize: hasValue ? theme.fontSizes[0] : theme.fontSizes[2],
      fontWeight: theme.fontWeights[0],
      paddingLeft: theme.space[3],
      paddingTop: theme.space[2],
      top: hasValue && theme.space[3],
      transform: hasValue ? 'translateY(-80%)' : 'none',
      color: theme.colors.steel[1],
      opacity: hasValue ? '0.7' : theme.opacity[0],

      'textarea:focus + &': {
        display: hideLabelOnFocus && !hasValue ? 'none' : 'block',
        fontSize: theme.fontSizes[0],
        transform: 'translateY(-80%)',
        fontWeight: theme.fontWeights[1],
        top: theme.space[3]
      }
    }}
    {...rest}
  />
)

const TextAreaField = React.forwardRef<
  HTMLTextAreaElement,
  {
    name?: string
    className?: string
    value: string | number
    label?: string
    placeholder?: string
    disabled?: boolean
    subtext?: string
    isValid?: boolean
    isInvalid?: boolean
    hideLabelOnFocus?: boolean
    onChange(event: React.ChangeEvent<HTMLTextAreaElement>): void
    nomargin?: boolean
    noLeftRadius?: boolean
    noRightRadius?: boolean
    focusOnMount?: boolean
    maxLength?: number
  }
>(
  (
    {
      name,
      value,
      label,
      disabled,
      isValid,
      isInvalid,
      className,
      hideLabelOnFocus,
      subtext,
      nomargin: noMargin,
      noLeftRadius,
      noRightRadius,
      ...rest
    },
    ref
  ) => {
    const textAreaHasValue = Boolean(value) && value !== ''
    const hasLabel = Boolean(label)

    return (
      <>
        <TextAreaContainer
          className={className}
          disabled={disabled}
          isInvalid={isInvalid}
          isValid={isValid}
          noLeftRadius={noLeftRadius}
          noMargin={noMargin}
          noRightRadius={noRightRadius}
          subtext={Boolean(subtext)}
        >
          <TextArea
            ref={ref}
            aria-label={label}
            className={className}
            disabled={disabled}
            hasLabel={hasLabel}
            hasValue={textAreaHasValue}
            hideLabelOnFocus={hideLabelOnFocus}
            name={name}
            type={undefined}
            value={value}
            {...rest}
          />
          {label && (
            <TextAreaLabel
              hasValue={textAreaHasValue}
              hideLabelOnFocus={hideLabelOnFocus}
            >
              {label}
            </TextAreaLabel>
          )}
        </TextAreaContainer>
        <FormSubtext isInvalid={isInvalid}>{subtext}</FormSubtext>
      </>
    )
  }
)

TextAreaField.displayName = 'TextAreaField'

export default TextAreaField
